import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useAudioSettings } from '../context/AudioSettingsContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { Camera } from '@capacitor/camera';
import { Browser } from '@capacitor/browser';
import OneSignal from 'onesignal-cordova-plugin';
import { Capacitor } from '@capacitor/core';
import { sendNewDeviceAlertNotification } from '../lib/notifications';
import GameBoy from '../components/GameBoy';
import ChefImage from '../assets/images/Chef.png';
import '../styles/gameboy.css';

// Audio files
const KANTO_INTRO = process.env.PUBLIC_URL + '/music/kanto-intro.mp3';
const SELECT_SOUND = process.env.PUBLIC_URL + '/music/Select.mp3';

// Story text chunks - User flow (individual with own email)
const USER_INTRO_CHUNKS = [
  ["Hello Trader!", "Welcome to CardChase!"],
  ["An app where you can connect", "with other traders and"],
  ["meet up to trade at", "CardChase Locations!"],
  ["My name is Chef, from the", "Trainer Center down in"],
  ["Huntington Beach, California!", ""],
  ["Let's get you set up!", ""],
  ["First, tell me about yourself...", ""]
];

// Story text chunks - Parent flow (family account manager)
const PARENT_INTRO_CHUNKS = [
  ["Hello!", "Welcome to CardChase!"],
  ["I see you're setting up", "a family account!"],
  ["Let's start with YOUR info", "as the account owner..."],
  ["Then we'll add your kids!", ""],
  ["My name is Chef!", "Let's get started..."]
];

// Legacy intro for existing users (signup_type is null)
const INTRO_CHUNKS = USER_INTRO_CHUNKS;

const PERMISSION_CHUNKS = [
  ["Almost done! You'll need", "camera access to take pics", "and add cards to binders.", "Sound good?"],
  ["Also, allow notifications", "so when you get a request,", "you'll be notified!", "Allow notifications too?"],
  ["Perfect! You're all set!", "Let's start trading!"]
];

// Screen content transition
const ScreenTransition = ({ children, screenKey }) => (
  <motion.div
    key={screenKey}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    style={{ width: '100%', height: '100%' }}
  >
    {children}
  </motion.div>
);

const GameScreen = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { refreshProfile, userProfile } = useProfile();
  const { theme, isDark } = useTheme();

  // Current screen: 'title' | 'options' | 'newgame' | 'onboarding'
  const [currentScreen, setCurrentScreen] = useState('title');
  const [intakeCompleted, setIntakeCompleted] = useState(null);
  const [loading, setLoading] = useState(true);

  // Title screen state
  const [titleOption, setTitleOption] = useState(0);

  // Options screen state
  const [optionsOption, setOptionsOption] = useState(0);
  const [skipTitleScreen, setSkipTitleScreen] = useState(false);
  // The account's audio settings, the same ones Preferences edits. This screen
  // used to keep its own copy in localStorage only, so Music: OFF here never
  // reached the account and the next launch turned it back on.
  const { settings: audioSettings, updateSetting: updateAudioSetting } = useAudioSettings();
  const musicEnabled = audioSettings.musicEnabled !== false;
  const soundEnabled = audioSettings.soundEnabled !== false;

  // Onboarding state
  const [mode, setMode] = useState('story'); // 'story' | 'form'
  const [storyPhase, setStoryPhase] = useState('intro'); // 'intro' | 'permissions'
  const [formStep, setFormStep] = useState(0);

  // Pokemon scroll state
  const [chunkIndex, setChunkIndex] = useState(0);
  const [allLines, setAllLines] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [typedLines, setTypedLines] = useState({});
  const [currentTypingLine, setCurrentTypingLine] = useState(0);
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  const [isTextBoxReady, setIsTextBoxReady] = useState(false);

  // Dialogs
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipOption, setSkipOption] = useState(0);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [logoutOption, setLogoutOption] = useState(1);

  // New game confirmation screen
  const [newGameOption, setNewGameOption] = useState(0);

  // Audio
  const bgMusicRef = useRef(null);
  // Read by timers and callbacks that were created before the account's
  // answer arrived, so they see the current value rather than a stale one.
  const musicEnabledRef = useRef(musicEnabled);
  useEffect(() => {
    musicEnabledRef.current = musicEnabled;
    // Off means off, even if the intro music started before the account said so.
    if (!musicEnabled && bgMusicRef.current) bgMusicRef.current.pause();
  }, [musicEnabled]);
  const [musicStarted, setMusicStarted] = useState(false);

  // Animation state
  const [characterVisible, setCharacterVisible] = useState(false);
  const [textBoxVisible, setTextBoxVisible] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', username: '', birthDate: '',
    parentFirstName: '', parentLastName: '', parentEmail: '', parentPhone: '',
    termsAgreed: false, privacyAgreed: false,
  });
  const [isMinor, setIsMinor] = useState(false);
  const [saving, setSaving] = useState(false);

  // Track if user has viewed legal docs (must tap to enable checkbox)
  const [hasViewedTerms, setHasViewedTerms] = useState(false);
  const [hasViewedPrivacy, setHasViewedPrivacy] = useState(false);

  // Parent/Child system state
  const [wantsToTrade, setWantsToTrade] = useState(true);       // minor's choice
  const [isParentAccount, setIsParentAccount] = useState(false); // adult's choice
  const [parentWillTrade, setParentWillTrade] = useState(false); // parent's choice
  const [addKidsNow, setAddKidsNow] = useState(false);          // add kids now or later
  const [addParentNow, setAddParentNow] = useState(false);      // minor: add parent now or later
  const [childProfiles, setChildProfiles] = useState([]);       // kids being added

  // Yes/No selection state for branching questions (0 = first option, 1 = second option)
  const [yesNoSelection, setYesNoSelection] = useState(0);

  // Signup type from sign-up screen ('user' | 'parent' | null for legacy)
  const [signupType, setSignupType] = useState(null);

  // Load skip title preference
  useEffect(() => {
    const saved = localStorage.getItem('skipTitleScreen');
    if (saved === 'true') setSkipTitleScreen(true);
  }, []);

  // Check intake status on mount
  useEffect(() => {
    const checkIntake = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking intake:', error);
        }

        const completed = data?.intake_completed && data?.first_name && data?.username;
        setIntakeCompleted(completed);

        // Read auth metadata to prefill fields from pre-auth wizard
        const meta = user?.user_metadata || {};

        // Set signup type - prefer DB, fallback to auth metadata for new accounts
        setSignupType(data?.signup_type || meta.signup_type || null);

        if (meta.birth_date) {
          const age = calculateAge(meta.birth_date);
          setIsMinor(age < 18);
          if (meta.signup_type === 'parent') {
            setIsParentAccount(true);
            setParentWillTrade(meta.parent_will_trade || false);
          }
        }

        // Prefill form data if exists
        if (data) {
          let displayDate = '';
          // Prefer metadata birth_date, fall back to DB
          const bd = meta.birth_date || data.birth_date;
          if (bd) {
            const parts = bd.split('-');
            if (parts.length === 3) displayDate = `${parts[1]}/${parts[2]}/${parts[0]}`;
          }
          setFormData(prev => ({
            ...prev,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            username: data.username || '',
            birthDate: displayDate,
            parentFirstName: data.parent_first_name || '',
            parentLastName: data.parent_last_name || '',
            parentEmail: data.parent_email || '',
            parentPhone: data.parent_phone || '',
            termsAgreed: data.contract_agreed || false,
            privacyAgreed: data.contract_agreed || false,
          }));
          if (bd) {
            const age = calculateAge(bd);
            setIsMinor(age < 18);
          }
        } else if (meta.birth_date) {
          // No DB row yet, but have metadata - prefill birthDate
          const parts = meta.birth_date.split('-');
          if (parts.length === 3) {
            setFormData(prev => ({
              ...prev,
              birthDate: `${parts[1]}/${parts[2]}/${parts[0]}`,
            }));
          }
        }
      } catch (err) {
        console.error('Error:', err);
        setIntakeCompleted(false);
      } finally {
        setLoading(false);
      }
    };

    checkIntake();
  }, [user?.id]);

  // Title menu options
  const titleOptions = intakeCompleted
    ? ['Continue', 'New Game', 'Options']
    : ['New Game', 'Options'];

  // Options menu options
  const getOptionsMenuItems = () => {
    const base = [
      { id: 'terms', label: 'Terms and Agreements' },
      { id: 'privacy', label: 'Privacy Policy' },
    ];
    if (user) {
      return [
        ...base,
        { id: 'music', label: `Music: ${musicEnabled ? 'ON' : 'OFF'}` },
        { id: 'sound', label: `Sound: ${soundEnabled ? 'ON' : 'OFF'}` },
        { id: 'skipTitle', label: `Skip Title: ${skipTitleScreen ? 'ON' : 'OFF'}` },
        { id: 'logout', label: 'Log Out' },
      ];
    }
    return [
      ...base,
      { id: 'music', label: `Music: ${musicEnabled ? 'ON' : 'OFF'}` },
      { id: 'sound', label: `Sound: ${soundEnabled ? 'ON' : 'OFF'}` },
    ];
  };
  const optionsMenuItems = getOptionsMenuItems();

  // Audio functions
  const playClickSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const clickAudio = new Audio(SELECT_SOUND);
      clickAudio.volume = 0.3;
      clickAudio.play().catch(() => {});
    } catch (err) {}
  }, [soundEnabled]);

  // kanto-intro.mp3 - respects music setting
  const startMusic = useCallback(() => {
    const musicOn = musicEnabledRef.current;
    if (bgMusicRef.current && bgMusicRef.current.paused && musicOn) {
      bgMusicRef.current.play()
        .then(() => setMusicStarted(true))
        .catch(() => {});
    }
  }, []);

  // Stop kanto music (call before navigating away)
  const stopMusic = useCallback(() => {
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current = null;
    }
    setMusicStarted(false);
  }, []);

  // Cleanup on unmount - ensures music stops when leaving GameScreen
  useEffect(() => {
    return () => {
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current = null;
      }
    };
  }, []);

  // Permission functions
  const requestCameraPermission = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        await Camera.requestPermissions();
      } else {
        await navigator.mediaDevices.getUserMedia({ video: true });
      }
    } catch (err) {
      console.log('Camera permission denied:', err);
    }
  };

  const requestNotificationPermission = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Use OneSignal to request permission (works on both iOS and Android)
        const granted = await OneSignal.Notifications.requestPermission(true);
        console.log('[OneSignal] Permission:', granted ? 'granted' : 'denied');

        // If granted, register device to Supabase
        if (granted) {
          await registerDeviceToSupabase();
        }
      } else {
        // Web fallback
        if ('Notification' in window) await Notification.requestPermission();
      }
    } catch (err) {
      console.log('Notification permission error:', err);
    }
  };

  // Register device to user_devices table after permission granted
  const registerDeviceToSupabase = async () => {
    try {
      console.log('[OneSignal] Starting device registration...');
      console.log('[OneSignal] User:', user?.id);

      if (!user) {
        console.log('[OneSignal] No user - aborting');
        return;
      }

      // Get user's database ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      console.log('[OneSignal] User data:', userData, 'Error:', userError);

      if (!userData) {
        console.log('[OneSignal] No user data found');
        return;
      }

      const platform = Capacitor.getPlatform();
      console.log('[OneSignal] Platform:', platform);

      // Wait for OneSignal to generate player ID
      // In v5 SDK, it's a property .id not a method .getId()
      let playerId = null;
      for (let i = 0; i < 10; i++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        try {
          // Try property access first (v5 SDK)
          playerId = OneSignal.User.pushSubscription.id;
          console.log('[OneSignal] .id attempt', i + 1, ':', playerId);
        } catch (e) {
          console.log('[OneSignal] .id error attempt', i + 1, ':', e);
        }
        if (playerId) break;
      }

      if (!playerId) {
        console.log('[OneSignal] No player ID after 5s');
        return;
      }

      console.log('[OneSignal] Player ID:', playerId);

      // Set external user ID in OneSignal (links to our user)
      await OneSignal.login(userData.id);
      console.log('[OneSignal] External user set:', userData.id);

      // Check if this device already exists
      const { data: existingDevice } = await supabase
        .from('user_devices')
        .select('id')
        .eq('onesignal_player_id', playerId)
        .single();

      const isNewDevice = !existingDevice;

      // Upsert device to our database
      const { error } = await supabase
        .from('user_devices')
        .upsert({
          user_id: userData.id,
          onesignal_player_id: playerId,
          platform: platform,
          device_name: `${platform} device`,
          last_active_at: new Date().toISOString()
        }, { onConflict: 'onesignal_player_id' });

      if (error) {
        console.error('[OneSignal] Device registration error:', error);
        return;
      }

      console.log('[OneSignal] Device registered:', playerId.substring(0, 20) + '...');

      // Send security alert for new device login
      if (isNewDevice) {
        const deviceInfo = `${platform.charAt(0).toUpperCase() + platform.slice(1)} device`;
        sendNewDeviceAlertNotification(userData.id, deviceInfo);
      }
    } catch (err) {
      console.error('[OneSignal] Device registration failed:', err);
    }
  };

  // Open legal documents in in-app browser
  const openLegalDoc = async (type) => {
    const baseUrl = window.location.origin;
    const url = type === 'terms' ? `${baseUrl}/terms` : `${baseUrl}/privacy`;

    try {
      if (Capacitor.isNativePlatform()) {
        // Open in-app browser on native
        await Browser.open({ url, presentationStyle: 'popover' });
      } else {
        // Open in new tab on web
        window.open(url, '_blank');
      }

      // Mark as viewed
      if (type === 'terms') {
        setHasViewedTerms(true);
      } else {
        setHasViewedPrivacy(true);
      }
    } catch (err) {
      console.error('Error opening legal doc:', err);
      // Fallback - try window.open
      window.open(url, '_blank');
      if (type === 'terms') {
        setHasViewedTerms(true);
      } else {
        setHasViewedPrivacy(true);
      }
    }
  };

  // Calculate age
  const calculateAge = (birthDate) => {
    const today = new Date();
    let birth;
    if (birthDate.includes('/')) {
      const parts = birthDate.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        birth = new Date(parts[2], parts[0] - 1, parts[1]);
      } else return 0;
    } else {
      birth = new Date(birthDate);
    }
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Save profile
  const saveProfile = async () => {
    setSaving(true);
    try {
      // Merge form data with auth metadata
      const meta = user?.user_metadata || {};
      const birthDateStr = formData.birthDate || '';
      let dbDate = meta.birth_date || null; // Default to metadata birth_date

      // If form has a birth date (legacy flow), use that instead
      if (birthDateStr && birthDateStr.includes('/')) {
        const parts = birthDateStr.split('/');
        if (parts.length === 3) {
          dbDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
      }

      const age = dbDate ? calculateAge(dbDate) : 0;

      const { data: upsertData, error } = await supabase.from('users').upsert({
        auth_id: user.id,
        email: user.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        username: formData.username,
        birth_date: dbDate,
        is_minor: age < 18 ? 'true' : 'false',
        // New parent/child system fields
        is_under_18: age < 18,
        wants_to_trade: wantsToTrade,
        is_parent: isParentAccount,
        parent_will_trade: parentWillTrade,
        signup_type: signupType || meta.signup_type || null,
        // Legacy parent contact fields (still used for records)
        parent_first_name: formData.parentFirstName || null,
        parent_last_name: formData.parentLastName || null,
        parent_email: formData.parentEmail || null,
        parent_phone: formData.parentPhone || null,
        intake_completed: true,
        contract_agreed: true,
        contract_agreed_at: new Date().toISOString(),
      }, { onConflict: 'auth_id' })
        .select('id')
        .single();

      if (error) throw error;

      // If parent with kids in metadata, create child rows + family_links
      const kids = meta.kids || [];
      if (isParentAccount && kids.length > 0 && upsertData?.id) {
        const parentId = upsertData.id;
        for (const kid of kids) {
          if (!kid.name || !kid.name.trim()) continue;

          // See AddChildModal: one transactional RPC rather than an insert
          // followed by a link, so a child profile is never left unowned.
          const { error: childError } = await supabase.rpc('create_child_profile', {
            p_parent_id: parentId,
            p_first_name: kid.name.trim(),
            p_can_trade: true,
          });

          if (childError) {
            console.error('Error creating child:', childError);
            continue;
          }
        }
      }

      // Stop music before leaving GameBoy screen
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current = null;
      }

      // Refresh profile context so canTrade is calculated correctly
      await refreshProfile();

      // Stop kanto music before navigating
      stopMusic();

      // Navigate based on user type
      if (isParentAccount) {
        // Parents (whether they trade or not) go to profile picker
        navigate('/profile-picker');
      } else {
        // Regular user (adult non-parent or minor): go to home
        navigate('/home');
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get current story chunks based on signup type
  const getCurrentChunks = useCallback(() => {
    if (storyPhase === 'permissions') return PERMISSION_CHUNKS;
    // Use different intro based on signup type
    if (signupType === 'parent') return PARENT_INTRO_CHUNKS;
    return USER_INTRO_CHUNKS; // Default for 'user' and legacy (null)
  }, [storyPhase, signupType]);

  // Initialize onboarding animations and audio
  const initializeOnboarding = useCallback(() => {
    // Clean up any existing audio first
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current = null;
    }

    // Setup audio
    const bgMusic = new Audio(KANTO_INTRO);
    bgMusic.loop = true;
    bgMusic.volume = 0.1;
    bgMusicRef.current = bgMusic;

    // Try auto-start (respects music setting)
    setTimeout(() => {
      const musicOn = musicEnabledRef.current;
      if (bgMusicRef.current && musicOn) {
        bgMusicRef.current.play().catch(() => {});
      }
    }, 100);

    // Fade in character and textbox
    setTimeout(() => {
      setCharacterVisible(true);
      setTextBoxVisible(true);
    }, 500);

    // Text box ready after 4s
    setTimeout(() => {
      setIsTextBoxReady(true);
    }, 4000);
  }, []);

  // Build allLines when chunk changes
  useEffect(() => {
    if (currentScreen !== 'onboarding' || mode !== 'story') return;
    const chunks = getCurrentChunks();
    if (chunkIndex < chunks.length) {
      setAllLines(chunks[chunkIndex]);
      setScrollPosition(0);
      setTypedLines({});
      setCurrentTypingLine(0);
      setTypingText('');
      setIsTyping(true);
      setShowCursor(false);
    }
  }, [chunkIndex, storyPhase, mode, currentScreen, getCurrentChunks]);

  // Typewriter effect
  useEffect(() => {
    if (currentScreen !== 'onboarding' || !isTextBoxReady || mode !== 'story' || showSkipDialog) return;

    if (isTyping && allLines[currentTypingLine]) {
      const targetText = allLines[currentTypingLine];
      if (typingText.length < targetText.length) {
        const timer = setTimeout(() => {
          setTypingText(targetText.slice(0, typingText.length + 1));
        }, 25);
        return () => clearTimeout(timer);
      } else {
        setTypedLines(prev => ({ ...prev, [currentTypingLine]: targetText }));
        setIsTyping(false);
        setShowCursor(true);
        if (currentTypingLine === 0 && scrollPosition === 0 && allLines[1]) {
          setTimeout(() => {
            setCurrentTypingLine(1);
            setTypingText('');
            setIsTyping(true);
            setShowCursor(false);
          }, 100);
        }
      }
    }
  }, [typingText, isTyping, allLines, currentTypingLine, scrollPosition, isTextBoxReady, mode, currentScreen, showSkipDialog]);

  // Cursor blink
  useEffect(() => {
    if (isTyping || currentScreen !== 'onboarding') return;
    const timer = setInterval(() => setShowCursor(prev => !prev), 500);
    return () => clearInterval(timer);
  }, [isTyping, currentScreen]);

  // Get line text
  const getLineText = (lineIndex) => {
    if (typedLines[lineIndex]) return typedLines[lineIndex];
    if (lineIndex === currentTypingLine && isTyping) return typingText;
    return '';
  };

  // Date input handler
  const handleDateInput = (value) => {
    const digits = value.replace(/\D/g, '');
    let formatted = '';
    if (digits.length > 0) formatted = digits.slice(0, 2);
    if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
    if (digits.length > 4) formatted += '/' + digits.slice(4, 8);
    setFormData(prev => ({ ...prev, birthDate: formatted }));
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ==================== BUTTON HANDLERS ====================

  const handleAPress = async () => {
    playClickSound();

    // --- NEW GAME SCREEN ---
    if (currentScreen === 'newgame') {
      if (newGameOption === 0) {
        stopMusic();
        await signOut();
        navigate('/login');
      } else {
        setCurrentScreen('title');
      }
      return;
    }

    // --- LOGOUT DIALOG ---
    if (showLogoutDialog) {
      if (logoutOption === 0) {
        stopMusic();
        signOut();
        navigate('/login');
      } else {
        setShowLogoutDialog(false);
      }
      return;
    }

    // --- SKIP DIALOG ---
    if (showSkipDialog) {
      if (skipOption === 0) {
        setShowSkipDialog(false);
        if (storyPhase === 'permissions') {
          await requestCameraPermission();
          await requestNotificationPermission();
          await saveProfile();
        } else {
          setMode('form');
          setFormStep(0);
        }
      } else {
        setShowSkipDialog(false);
      }
      return;
    }

    // --- TITLE SCREEN ---
    if (currentScreen === 'title') {
      const option = titleOptions[titleOption];
      if (option === 'Continue') {
        stopMusic();
        navigate('/home');
      } else if (option === 'New Game') {
        if (intakeCompleted) {
          // Returning player - go to confirmation screen
          setCurrentScreen('newgame');
          setNewGameOption(0);
        } else {
          // Fresh player - go to onboarding
          setCurrentScreen('onboarding');
          initializeOnboarding();
        }
      } else if (option === 'Options') {
        setCurrentScreen('options');
        setOptionsOption(0);
      }
      return;
    }

    // --- OPTIONS SCREEN ---
    if (currentScreen === 'options') {
      const option = optionsMenuItems[optionsOption];
      if (option.id === 'terms') {
        stopMusic();
        navigate('/terms');
      } else if (option.id === 'privacy') {
        stopMusic();
        navigate('/privacy');
      } else if (option.id === 'music') {
        const newValue = !musicEnabled;
        updateAudioSetting('musicEnabled', newValue);
        // Immediately pause/resume kanto music
        if (!newValue && bgMusicRef.current) {
          bgMusicRef.current.pause();
        } else if (newValue && bgMusicRef.current && currentScreen === 'onboarding') {
          bgMusicRef.current.play().catch(() => {});
        }
      } else if (option.id === 'sound') {
        updateAudioSetting('soundEnabled', !soundEnabled);
      } else if (option.id === 'skipTitle') {
        const newValue = !skipTitleScreen;
        setSkipTitleScreen(newValue);
        localStorage.setItem('skipTitleScreen', newValue.toString());
        if (userProfile?.id) {
          supabase.from('users').update({ pref_skip_title_screen: newValue }).eq('id', userProfile.id).then(() => {});
        }
      } else if (option.id === 'logout') {
        stopMusic();
        await signOut();
        navigate('/login');
      }
      return;
    }

    // --- ONBOARDING SCREEN ---
    if (currentScreen === 'onboarding') {
      // Music only starts on onboarding interaction
      startMusic();

      if (mode === 'story') {
        // Guard: Don't advance if text isn't ready or still typing
        if (!isTextBoxReady) {
          // Textbox not ready yet - just play sound, do nothing
          return;
        }

        if (isTyping) {
          // Currently typing - complete the current line instantly
          // The useEffect will handle starting the next line automatically
          if (allLines[currentTypingLine]) {
            const targetText = allLines[currentTypingLine];
            setTypingText(targetText);
            // Note: Don't set typedLines or isTyping here - let the useEffect do it
            // This ensures consistent state transitions
          }
          return;
        }

        // Check if all visible lines are typed before allowing advance
        const visibleLine1 = scrollPosition;
        const visibleLine2 = scrollPosition + 1;
        const line1Done = !allLines[visibleLine1] || typedLines[visibleLine1];
        const line2Done = !allLines[visibleLine2] || typedLines[visibleLine2];

        if (!line1Done || !line2Done || !showCursor) {
          // Not ready to advance - visible lines still need typing
          return;
        }

        const chunks = getCurrentChunks();
        if (scrollPosition + 2 < allLines.length) {
          const newScrollPosition = scrollPosition + 1;
          setScrollPosition(newScrollPosition);
          const newBottomLineIndex = newScrollPosition + 1;
          if (allLines[newBottomLineIndex] && !typedLines[newBottomLineIndex]) {
            setCurrentTypingLine(newBottomLineIndex);
            setTypingText('');
            setIsTyping(true);
            setShowCursor(false);
          } else {
            setShowCursor(true);
          }
        } else {
          if (chunkIndex < chunks.length - 1) {
            if (storyPhase === 'permissions') {
              if (chunkIndex === 0) await requestCameraPermission();
              else if (chunkIndex === 1) await requestNotificationPermission();
            }
            setChunkIndex(chunkIndex + 1);
          } else {
            if (storyPhase === 'intro') {
              setMode('form');
              setFormStep(0);
            } else {
              await saveProfile();
            }
          }
        }
      } else if (mode === 'form') {
        // Form submission - flow depends on signupType
        // NEW FLOW (signupType === 'user'):
        //   0=Name, 1=Birthday, 2=Terms → Home
        // NEW FLOW (signupType === 'parent'):
        //   0=Name, 1=Birthday, 2=WillTrade, 3=AddKids, 4=Terms → ProfilePicker
        // LEGACY FLOW (signupType === null):
        //   0=Name, 1=Birthday, 2=BranchQuestion, 3=ParentEmail/WillTrade, 4=AddKidsNow, 5=AddKids, 6=Terms

        if (signupType === 'user') {
          // User flow (streamlined): 0=Name+Username → 1=Terms
          if (formStep === 0) {
            if (!formData.firstName || !formData.lastName || !formData.username) return;
            setFormStep(1); // Go to Terms
          } else if (formStep === 1) {
            // Terms
            if (!formData.termsAgreed || !formData.privacyAgreed) return;
            setStoryPhase('permissions');
            setChunkIndex(0);
            setScrollPosition(0);
            setTypedLines({});
            setCurrentTypingLine(0);
            setTypingText('');
            setIsTyping(true);
            setMode('story');
          }
        } else if (signupType === 'parent') {
          // Parent flow (streamlined): 0=Name+Username → 1=Terms
          if (formStep === 0) {
            if (!formData.firstName || !formData.lastName || !formData.username) return;
            setFormStep(1); // Go to Terms
          } else if (formStep === 1) {
            // Terms
            if (!formData.termsAgreed || !formData.privacyAgreed) return;
            setStoryPhase('permissions');
            setChunkIndex(0);
            setScrollPosition(0);
            setTypedLines({});
            setCurrentTypingLine(0);
            setTypingText('');
            setIsTyping(true);
            setMode('story');
          }
        } else {
          // Legacy flow (signupType === null) - keep existing behavior
          // Steps: 0=Name, 1=Birthday, 2=BranchQuestion, 3=ParentEmail/WillTrade, 4=AddKidsNow, 5=AddKids, 6=Terms
          if (formStep === 0) {
            if (!formData.firstName || !formData.lastName || !formData.username) return;
            setFormStep(1);
          } else if (formStep === 1) {
            if (!formData.birthDate || formData.birthDate.length !== 10) return;
            const age = calculateAge(formData.birthDate);
            setIsMinor(age < 18);
            setFormStep(2);
          } else if (formStep === 2) {
            if (isMinor) {
              const selectedWantsToTrade = yesNoSelection === 0;
              setWantsToTrade(selectedWantsToTrade);
              setYesNoSelection(0);
              if (selectedWantsToTrade) {
                setFormStep(3);
              } else {
                setFormStep(6);
              }
            } else {
              const selectedIsParent = yesNoSelection === 0;
              setIsParentAccount(selectedIsParent);
              setYesNoSelection(0);
              if (selectedIsParent) {
                setFormStep(3);
              } else {
                setFormStep(6);
              }
            }
          } else if (formStep === 3) {
            if (isMinor) {
              const selectedAddNow = yesNoSelection === 0;
              setAddParentNow(selectedAddNow);
              setYesNoSelection(0);
              if (selectedAddNow) {
                setFormStep(4);
              } else {
                setFormStep(6);
              }
            } else {
              const selectedWillTrade = yesNoSelection === 0;
              setParentWillTrade(selectedWillTrade);
              setYesNoSelection(0);
              setFormStep(4);
            }
          } else if (formStep === 4) {
            if (isMinor) {
              setFormStep(6);
            } else {
              const selectedAddNow = yesNoSelection === 0;
              setAddKidsNow(selectedAddNow);
              setYesNoSelection(0);
              if (selectedAddNow) {
                setFormStep(5);
              } else {
                setFormStep(6);
              }
            }
          } else if (formStep === 5) {
            setFormStep(6);
          } else if (formStep === 6) {
            if (!formData.termsAgreed || !formData.privacyAgreed) return;
            setStoryPhase('permissions');
            setChunkIndex(0);
            setScrollPosition(0);
            setTypedLines({});
            setCurrentTypingLine(0);
            setTypingText('');
            setIsTyping(true);
            setMode('story');
          }
        }
      }
    }
  };

  const handleBPress = () => {
    playClickSound();

    if (currentScreen === 'newgame') {
      setCurrentScreen('title');
      return;
    }
    if (showLogoutDialog) {
      setShowLogoutDialog(false);
      return;
    }
    if (showSkipDialog) {
      setShowSkipDialog(false);
      return;
    }

    if (currentScreen === 'options') {
      setCurrentScreen('title');
      return;
    }

    if (currentScreen === 'onboarding') {
      if (mode === 'story' && storyPhase === 'permissions') {
        // Go back to the terms step based on flow
        setMode('form');
        if (signupType === 'user') {
          setFormStep(1); // Terms is step 1 in streamlined user flow
        } else if (signupType === 'parent') {
          setFormStep(1); // Terms is step 1 in streamlined parent flow
        } else {
          setFormStep(6); // Terms is step 6 in legacy flow
        }
        return;
      }
      if (mode === 'form') {
        if (signupType === 'user') {
          // User flow (streamlined): 0=Name+Username → 1=Terms
          if (formStep === 0) {
            setCurrentScreen('title');
            setMode('story');
            setStoryPhase('intro');
            setChunkIndex(0);
          } else if (formStep === 1) {
            setFormStep(0);
          }
        } else if (signupType === 'parent') {
          // Parent flow (streamlined): 0=Name+Username → 1=Terms
          if (formStep === 0) {
            setCurrentScreen('title');
            setMode('story');
            setStoryPhase('intro');
            setChunkIndex(0);
          } else if (formStep === 1) {
            setFormStep(0);
          }
        } else {
          // Legacy flow: 0=Name, 1=Birthday, 2=BranchQuestion, 3=ParentEmail/WillTrade, 4=AddKidsNow, 5=AddKids, 6=Terms
          if (formStep === 0) {
            setCurrentScreen('title');
            setMode('story');
            setStoryPhase('intro');
            setChunkIndex(0);
          } else if (formStep === 1) {
            setFormStep(0);
          } else if (formStep === 2) {
            setFormStep(1);
          } else if (formStep === 3) {
            setFormStep(2);
          } else if (formStep === 4) {
            setFormStep(3);
          } else if (formStep === 5) {
            setFormStep(4);
          } else if (formStep === 6) {
            // Terms - go back based on path taken
            if (isMinor) {
              if (wantsToTrade) {
                if (addParentNow) {
                  setFormStep(4);
                } else {
                  setFormStep(3);
                }
              } else {
                setFormStep(2);
              }
            } else if (isParentAccount) {
              if (addKidsNow) {
                setFormStep(5);
              } else {
                setFormStep(4);
              }
            } else {
              setFormStep(2);
            }
          }
        }
      }
    }
  };

  const handleUpPress = () => {
    if (currentScreen === 'newgame') {
      playClickSound();
      setNewGameOption(0);
    } else if (showLogoutDialog) {
      playClickSound();
      setLogoutOption(0);
    } else if (showSkipDialog) {
      playClickSound();
      setSkipOption(0);
    } else if (currentScreen === 'title') {
      if (titleOption > 0) {
        playClickSound();
        setTitleOption(prev => prev - 1);
      }
    } else if (currentScreen === 'options') {
      if (optionsOption > 0) {
        playClickSound();
        setOptionsOption(prev => prev - 1);
      }
    } else if (currentScreen === 'onboarding' && mode === 'form') {
      // Yes/No selection for branching questions - legacy flow only
      const hasYesNo = (signupType === null && [2, 3, 4].includes(formStep));
      if (hasYesNo && yesNoSelection > 0) {
        playClickSound();
        setYesNoSelection(0);
      }
    }
  };

  const handleDownPress = () => {
    if (currentScreen === 'newgame') {
      playClickSound();
      setNewGameOption(1);
    } else if (showLogoutDialog) {
      playClickSound();
      setLogoutOption(1);
    } else if (showSkipDialog) {
      playClickSound();
      setSkipOption(1);
    } else if (currentScreen === 'title') {
      if (titleOption < titleOptions.length - 1) {
        playClickSound();
        setTitleOption(prev => prev + 1);
      }
    } else if (currentScreen === 'options') {
      if (optionsOption < optionsMenuItems.length - 1) {
        playClickSound();
        setOptionsOption(prev => prev + 1);
      }
    } else if (currentScreen === 'onboarding' && mode === 'form') {
      // Yes/No selection for branching questions - legacy flow only
      const hasYesNo = (signupType === null && [2, 3, 4].includes(formStep));
      if (hasYesNo && yesNoSelection < 1) {
        playClickSound();
        setYesNoSelection(1);
      }
    }
  };

  const handleSelectPress = () => {
    // SELECT only works on onboarding story mode (skip dialog)
    if (currentScreen === 'onboarding' && mode === 'story' && !showSkipDialog && !showLogoutDialog) {
      playClickSound();
      startMusic();
      setShowSkipDialog(true);
      setSkipOption(0);
    }
  };

  const handleStartPress = () => {
    // START only works on onboarding (logout dialog)
    if (currentScreen === 'onboarding' && !showSkipDialog && !showLogoutDialog) {
      playClickSound();
      startMusic();
      setShowLogoutDialog(true);
      setLogoutOption(1);
    }
  };

  const handlePowerOff = () => {
    if (bgMusicRef.current) bgMusicRef.current.pause();
  };

  const handlePowerToggle = () => {
    // Stop music but don't restart it - music only starts on onboarding
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current.currentTime = 0;
    }
    setMusicStarted(false);

    // Reset to title
    setCurrentScreen('title');
    setTitleOption(0);
    setMode('story');
    setStoryPhase('intro');
    setChunkIndex(0);
    setCharacterVisible(false);
    setTextBoxVisible(false);
    setIsTextBoxReady(false);
  };

  // ==================== RENDER FUNCTIONS ====================

  const displayName = formData.firstName
    ? `${formData.firstName}${formData.lastName ? ' ' + formData.lastName : ''}`
    : '';

  const renderTitleScreen = () => (
    <div className="gb-screen-content">
      <div className="gb-title-screen">
        <div className="gb-menu-box">
          <div className="gb-menu-header">CardChase</div>
          {titleOptions.map((option, index) => (
            <div key={option} className="gb-menu-option">
              <span className="gb-menu-cursor">{titleOption === index ? '>' : ' '}</span>
              {option}
            </div>
          ))}
          <div className="gb-menu-hint">Please Use Console Controls</div>
        </div>
      </div>
    </div>
  );

  const renderNewGameScreen = () => (
    <div className="gb-screen-content">
      <div className="gb-title-screen">
        <div className="gb-menu-box">
          <div className="gb-menu-header">New Game</div>
          <div className="gb-menu-message">Are you sure you want to log out and start a new account?</div>
          <div className="gb-menu-option">
            <span className="gb-menu-cursor">{newGameOption === 0 ? '>' : ' '}</span>
            Yes
          </div>
          <div className="gb-menu-option">
            <span className="gb-menu-cursor">{newGameOption === 1 ? '>' : ' '}</span>
            No
          </div>
          <div className="gb-menu-hint">B: Back</div>
        </div>
      </div>
    </div>
  );

  const renderOptionsScreen = () => (
    <div className="gb-screen-content">
      <div className="gb-title-screen">
        <div className="gb-menu-box gb-options-menu">
          <div className="gb-menu-header">OPTIONS</div>
          {optionsMenuItems.map((option, index) => (
            <div key={option.id} className="gb-menu-option">
              <span className="gb-menu-cursor">{optionsOption === index ? '>' : ' '}</span>
              {option.label}
            </div>
          ))}
          <div className="gb-menu-hint">B: Back</div>
        </div>
      </div>
    </div>
  );

  const renderOnboardingScreen = () => {
    // Logout dialog overlay
    if (showLogoutDialog) {
      return (
        <div className="gb-screen-content">
          <div className="gb-story">
            <div className="gb-character-area">
              <img src={ChefImage} alt="Chef" className={`gb-chef-image ${characterVisible ? 'visible' : ''}`} />
            </div>
            <div className={`gb-textbox ${textBoxVisible ? 'visible' : ''}`}>
              <div className="gb-visible-window">
                <div className="gb-line">{getLineText(scrollPosition)}</div>
                <div className="gb-line">{getLineText(scrollPosition + 1)}</div>
              </div>
            </div>
          </div>
          <div className="gb-skip-overlay">
            <div className="gb-skip-dialog">
              <div className="gb-skip-text">Are you sure you want to log out?</div>
              <div className="gb-skip-options">
                <div className="gb-skip-option">
                  <span className="gb-skip-cursor">{logoutOption === 0 ? '>' : ' '}</span>Yes
                </div>
                <div className="gb-skip-option">
                  <span className="gb-skip-cursor">{logoutOption === 1 ? '>' : ' '}</span>No
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Skip dialog overlay
    if (showSkipDialog) {
      return (
        <div className="gb-screen-content">
          <div className="gb-story">
            <div className="gb-character-area">
              <img src={ChefImage} alt="Chef" className={`gb-chef-image ${characterVisible ? 'visible' : ''}`} />
            </div>
            <div className={`gb-textbox ${textBoxVisible ? 'visible' : ''}`}>
              <div className="gb-visible-window">
                <div className="gb-line">{getLineText(scrollPosition)}</div>
                <div className="gb-line">{getLineText(scrollPosition + 1)}</div>
              </div>
            </div>
          </div>
          <div className="gb-skip-overlay">
            <div className="gb-skip-dialog">
              <div className="gb-skip-text">Skip the introduction?</div>
              <div className="gb-skip-options">
                <div className="gb-skip-option">
                  <span className="gb-skip-cursor">{skipOption === 0 ? '>' : ' '}</span>Yes
                </div>
                <div className="gb-skip-option">
                  <span className="gb-skip-cursor">{skipOption === 1 ? '>' : ' '}</span>No
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Story mode
    if (mode === 'story') {
      return (
        <div className="gb-screen-content fade-in">
          <div className="gb-story">
            <div className="gb-character-area">
              <img src={ChefImage} alt="Chef" className={`gb-chef-image ${characterVisible ? 'visible' : ''}`} />
            </div>
            <div className={`gb-textbox ${textBoxVisible ? 'visible' : ''}`}>
              <div className="gb-visible-window">
                <div className="gb-line">{getLineText(scrollPosition)}</div>
                <div className="gb-line">{getLineText(scrollPosition + 1)}</div>
              </div>
              {!isTyping && <span className="gb-cursor">{showCursor ? '\u25BC' : ''}</span>}
            </div>
          </div>
        </div>
      );
    }

    // Form mode - step titles depend on signupType
    const getFormTitle = () => {
      if (signupType === 'user') {
        // Streamlined user flow: 0=Name+Username, 1=Terms
        if (formStep === 0) return 'Your Info';
        if (formStep === 1) return 'Terms';
      } else if (signupType === 'parent') {
        // Streamlined parent flow: 0=Name+Username, 1=Terms
        if (formStep === 0) return 'Your Info';
        if (formStep === 1) return 'Terms';
      } else {
        // Legacy flow
        if (formStep === 0) return 'Your Info';
        if (formStep === 1) return 'Birthday';
        if (formStep === 2) return isMinor ? 'Trading' : 'Account Type';
        if (formStep === 3) return isMinor ? 'Add Parent' : 'Your Trading';
        if (formStep === 4) return isMinor ? 'Parent Email' : 'Add Kids';
        if (formStep === 5) return 'Kid Profiles';
        if (formStep === 6) return 'Terms';
      }
      return '';
    };

    return (
      <div className="gb-screen-content">
        <div className="gb-form">
          <div className="gb-form-header">
            <h2 className="gb-form-title">{getFormTitle()}</h2>
            <p className="gb-form-email">{user?.email}</p>
            {displayName && <p className="gb-form-name">{displayName}</p>}
          </div>

          <div className="gb-form-body">
            {/* Common Step 0: Name/Username (all flows) */}
            {formStep === 0 && (
              <>
                {signupType === 'parent' && (
                  <p className="gb-choice-note" style={{ marginBottom: 12 }}>
                    This is YOUR info as the account owner
                  </p>
                )}
                <div className="gb-input-row">
                  <div className="gb-input-group">
                    <label className="gb-label">First Name</label>
                    <input type="text" className="gb-input" value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)} placeholder="First" />
                  </div>
                  <div className="gb-input-group">
                    <label className="gb-label">Last Name</label>
                    <input type="text" className="gb-input" value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)} placeholder="Last" />
                  </div>
                </div>
                <div className="gb-input-group">
                  <label className="gb-label">Username</label>
                  <input type="text" className="gb-input" value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                    placeholder="Pick a username" />
                </div>
              </>
            )}

            {/* LEGACY FLOW - Step 1: Birthday */}
            {signupType === null && formStep === 1 && (
              <div className="gb-input-group">
                <label className="gb-label">Date of Birth</label>
                <input type="text" inputMode="numeric" className="gb-input" value={formData.birthDate}
                  onChange={(e) => handleDateInput(e.target.value)} placeholder="MM/DD/YYYY" maxLength={10} />
              </div>
            )}

            {/* USER FLOW - Step 1: Terms (streamlined) */}
            {signupType === 'user' && formStep === 1 && (
              <>
                {isMinor && (
                  <p className="gb-choice-note" style={{ marginBottom: 12, color: '#f59e0b' }}>
                    You can link to a parent in Profile to enable trading
                  </p>
                )}
                <p className="gb-choice-note" style={{ marginBottom: 8, fontSize: '8px' }}>
                  Tap each link to read before agreeing
                </p>
                <div className="gb-checkbox-group">
                  <input
                    type="checkbox"
                    className="gb-checkbox"
                    checked={formData.termsAgreed}
                    onChange={(e) => handleInputChange('termsAgreed', e.target.checked)}
                    disabled={!hasViewedTerms}
                    style={{ opacity: hasViewedTerms ? 1 : 0.4 }}
                  />
                  <span className="gb-checkbox-label">
                    I agree to the{' '}
                    <span
                      className="gb-legal-link"
                      onClick={() => openLegalDoc('terms')}
                      style={{ cursor: 'pointer', textDecoration: hasViewedTerms ? 'none' : 'underline' }}
                    >
                      Terms of Service {hasViewedTerms ? '(read)' : '(tap to read)'}
                    </span>
                  </span>
                </div>
                <div className="gb-checkbox-group">
                  <input
                    type="checkbox"
                    className="gb-checkbox"
                    checked={formData.privacyAgreed}
                    onChange={(e) => handleInputChange('privacyAgreed', e.target.checked)}
                    disabled={!hasViewedPrivacy}
                    style={{ opacity: hasViewedPrivacy ? 1 : 0.4 }}
                  />
                  <span className="gb-checkbox-label">
                    I agree to the{' '}
                    <span
                      className="gb-legal-link"
                      onClick={() => openLegalDoc('privacy')}
                      style={{ cursor: 'pointer', textDecoration: hasViewedPrivacy ? 'none' : 'underline' }}
                    >
                      Privacy Policy {hasViewedPrivacy ? '(read)' : '(tap to read)'}
                    </span>
                  </span>
                </div>
              </>
            )}

            {/* PARENT FLOW - Step 1: Terms (streamlined) */}
            {signupType === 'parent' && formStep === 1 && (
              <>
                <p className="gb-choice-note" style={{ marginBottom: 8, fontSize: '8px' }}>
                  Tap each link to read before agreeing
                </p>
                <div className="gb-checkbox-group">
                  <input
                    type="checkbox"
                    className="gb-checkbox"
                    checked={formData.termsAgreed}
                    onChange={(e) => handleInputChange('termsAgreed', e.target.checked)}
                    disabled={!hasViewedTerms}
                    style={{ opacity: hasViewedTerms ? 1 : 0.4 }}
                  />
                  <span className="gb-checkbox-label">
                    I agree to the{' '}
                    <span
                      className="gb-legal-link"
                      onClick={() => openLegalDoc('terms')}
                      style={{ cursor: 'pointer', textDecoration: hasViewedTerms ? 'none' : 'underline' }}
                    >
                      Terms of Service {hasViewedTerms ? '(read)' : '(tap to read)'}
                    </span>
                  </span>
                </div>
                <div className="gb-checkbox-group">
                  <input
                    type="checkbox"
                    className="gb-checkbox"
                    checked={formData.privacyAgreed}
                    onChange={(e) => handleInputChange('privacyAgreed', e.target.checked)}
                    disabled={!hasViewedPrivacy}
                    style={{ opacity: hasViewedPrivacy ? 1 : 0.4 }}
                  />
                  <span className="gb-checkbox-label">
                    I agree to the{' '}
                    <span
                      className="gb-legal-link"
                      onClick={() => openLegalDoc('privacy')}
                      style={{ cursor: 'pointer', textDecoration: hasViewedPrivacy ? 'none' : 'underline' }}
                    >
                      Privacy Policy {hasViewedPrivacy ? '(read)' : '(tap to read)'}
                    </span>
                  </span>
                </div>
              </>
            )}

            {/* LEGACY FLOW - Step 2: Branching Question */}
            {signupType === null && formStep === 2 && (
              <div className="gb-choice-group">
                {isMinor ? (
                  <>
                    <p className="gb-choice-question">Do you want to connect with other traders?</p>
                    <p className="gb-choice-note">(Trading requires parent permission)</p>
                  </>
                ) : (
                  <p className="gb-choice-question">Are you a parent adding kids to this account?</p>
                )}
                <div className="gb-yesno-options">
                  <div className="gb-yesno-option" onClick={() => { playClickSound(); setYesNoSelection(0); }}>
                    <span className="gb-yesno-cursor">{yesNoSelection === 0 ? '>' : '\u00A0'}</span>Yes
                  </div>
                  <div className="gb-yesno-option" onClick={() => { playClickSound(); setYesNoSelection(1); }}>
                    <span className="gb-yesno-cursor">{yesNoSelection === 1 ? '>' : '\u00A0'}</span>No
                  </div>
                </div>
              </div>
            )}

            {/* LEGACY FLOW - Step 3: Add Parent Now? (minor) OR Will You Trade (adult parent) */}
            {signupType === null && formStep === 3 && (
              <>
                {isMinor ? (
                  <div className="gb-choice-group">
                    <p className="gb-choice-question">Add your parent's email now?</p>
                    <p className="gb-choice-note">(You can always add it later in Profile)</p>
                    <div className="gb-yesno-options">
                      <div className="gb-yesno-option" onClick={() => { playClickSound(); setYesNoSelection(0); }}>
                        <span className="gb-yesno-cursor">{yesNoSelection === 0 ? '>' : '\u00A0'}</span>Now
                      </div>
                      <div className="gb-yesno-option" onClick={() => { playClickSound(); setYesNoSelection(1); }}>
                        <span className="gb-yesno-cursor">{yesNoSelection === 1 ? '>' : '\u00A0'}</span>Later
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="gb-choice-group">
                    <p className="gb-choice-question">Will you also be trading cards yourself?</p>
                    <p className="gb-choice-note">(You can always change this later)</p>
                    <div className="gb-yesno-options">
                      <div className="gb-yesno-option" onClick={() => { playClickSound(); setYesNoSelection(0); }}>
                        <span className="gb-yesno-cursor">{yesNoSelection === 0 ? '>' : '\u00A0'}</span>Yes
                      </div>
                      <div className="gb-yesno-option" onClick={() => { playClickSound(); setYesNoSelection(1); }}>
                        <span className="gb-yesno-cursor">{yesNoSelection === 1 ? '>' : '\u00A0'}</span>No
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* LEGACY FLOW - Step 4: Parent Email (minor) OR Add Kids Now? (adult parent) */}
            {signupType === null && formStep === 4 && (
              <>
                {isMinor ? (
                  <>
                    <p className="gb-choice-note">Enter your parent's email to unlock trading</p>
                    <div className="gb-input-group">
                      <label className="gb-label">Parent Email</label>
                      <input type="email" className="gb-input" value={formData.parentEmail}
                        onChange={(e) => handleInputChange('parentEmail', e.target.value)} placeholder="parent@email.com" />
                    </div>
                  </>
                ) : (
                  <div className="gb-choice-group">
                    <p className="gb-choice-question">Would you like to add your kids now?</p>
                    <p className="gb-choice-note">(You can always add them later in Profile)</p>
                    <div className="gb-yesno-options">
                      <div className="gb-yesno-option" onClick={() => { playClickSound(); setYesNoSelection(0); }}>
                        <span className="gb-yesno-cursor">{yesNoSelection === 0 ? '>' : '\u00A0'}</span>Now
                      </div>
                      <div className="gb-yesno-option" onClick={() => { playClickSound(); setYesNoSelection(1); }}>
                        <span className="gb-yesno-cursor">{yesNoSelection === 1 ? '>' : '\u00A0'}</span>Later
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* LEGACY FLOW - Step 5: Add Kids Form (placeholder) */}
            {signupType === null && formStep === 5 && (
              <div className="gb-choice-group">
                <p className="gb-choice-note">Add kids feature coming soon!</p>
                <p className="gb-choice-note">You can add kids from your Profile after setup.</p>
              </div>
            )}

            {/* LEGACY FLOW - Step 6: Terms */}
            {signupType === null && formStep === 6 && (
              <>
                <p className="gb-choice-note" style={{ marginBottom: 8, fontSize: '8px' }}>
                  Tap each link to read before agreeing
                </p>
                <div className="gb-checkbox-group">
                  <input
                    type="checkbox"
                    className="gb-checkbox"
                    checked={formData.termsAgreed}
                    onChange={(e) => handleInputChange('termsAgreed', e.target.checked)}
                    disabled={!hasViewedTerms}
                    style={{ opacity: hasViewedTerms ? 1 : 0.4 }}
                  />
                  <span className="gb-checkbox-label">
                    I agree to the{' '}
                    <span
                      className="gb-legal-link"
                      onClick={() => openLegalDoc('terms')}
                      style={{ cursor: 'pointer', textDecoration: hasViewedTerms ? 'none' : 'underline' }}
                    >
                      Terms of Service {hasViewedTerms ? '(read)' : '(tap to read)'}
                    </span>
                  </span>
                </div>
                <div className="gb-checkbox-group">
                  <input
                    type="checkbox"
                    className="gb-checkbox"
                    checked={formData.privacyAgreed}
                    onChange={(e) => handleInputChange('privacyAgreed', e.target.checked)}
                    disabled={!hasViewedPrivacy}
                    style={{ opacity: hasViewedPrivacy ? 1 : 0.4 }}
                  />
                  <span className="gb-checkbox-label">
                    I agree to the{' '}
                    <span
                      className="gb-legal-link"
                      onClick={() => openLegalDoc('privacy')}
                      style={{ cursor: 'pointer', textDecoration: hasViewedPrivacy ? 'none' : 'underline' }}
                    >
                      Privacy Policy {hasViewedPrivacy ? '(read)' : '(tap to read)'}
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="gb-form-footer">
            <p className="gb-form-hint">
              {(() => {
                // Determine if this is the terms step (last form step before permissions)
                const isTermsStep = (signupType === 'user' && formStep === 1) ||
                                    (signupType === 'parent' && formStep === 1) ||
                                    (signupType === null && formStep === 6);
                if (isTermsStep) {
                  return saving ? 'Saving...' : 'Press A to finish';
                }
                return 'Press A to continue';
              })()}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderScreenContent = () => {
    if (loading) {
      return (
        <div className="gb-screen-content">
          <div className="gb-title-screen">
            <div className="gb-menu-box">
              <div className="gb-menu-header">CardChase</div>
              <div className="gb-menu-option">Loading...</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <AnimatePresence mode="wait">
        {currentScreen === 'title' && (
          <ScreenTransition screenKey="title">{renderTitleScreen()}</ScreenTransition>
        )}
        {currentScreen === 'newgame' && (
          <ScreenTransition screenKey="newgame">{renderNewGameScreen()}</ScreenTransition>
        )}
        {currentScreen === 'options' && (
          <ScreenTransition screenKey="options">{renderOptionsScreen()}</ScreenTransition>
        )}
        {currentScreen === 'onboarding' && (
          <ScreenTransition screenKey="onboarding">{renderOnboardingScreen()}</ScreenTransition>
        )}
      </AnimatePresence>
    );
  };

  return (
    <GameBoy
      onAPress={handleAPress}
      onBPress={handleBPress}
      onUpPress={handleUpPress}
      onDownPress={handleDownPress}
      onSelectPress={handleSelectPress}
      onStartPress={handleStartPress}
      onPowerToggle={handlePowerToggle}
      onPowerOff={handlePowerOff}
    >
      {renderScreenContent()}
    </GameBoy>
  );
};

export default GameScreen;
